import React, { useState } from "react";
import {
  Modal,
  Button,
  Group,
  Stack,
  Title,
  Alert,
  PasswordInput,
  PinInput,
  Text,
  Divider,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconLock,
  IconShieldCheck,
} from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import { handleAuthError, handleGenericError } from "../../utils/errorHandler";

function SetSecurityPinModal({ isOpen, onClose, onUpdate, isReset = false }) {
  const { getVerifiedJWT } = useAuth();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    oldPin: "",
    password: "",
    pin: "",
  });

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate inputs
      if (isReset && (!formData.oldPin || formData.oldPin.length !== 4)) {
        setError("Please enter your current 4-digit PIN");
        return;
      }

      if (!formData.password) {
        setError("Password is required");
        return;
      }

      if (!formData.pin || formData.pin.length !== 4) {
        setError("Please enter a 4-digit PIN");
        return;
      }

      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setError("Authentication failed");
        return;
      }

      // Verify old PIN if resetting. /v1/user/verify-pin is the only endpoint
      // that checks a PIN; a wrong one still answers 200, so the verified flag
      // is what decides.
      if (isReset) {
        const verifyResponse = await fetch(
          getApiEndpoint("/v1/user/verify-pin"),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
            body: JSON.stringify({ pin: parseInt(formData.oldPin) }),
          }
        );

        const verifyData = await verifyResponse.json().catch(() => ({}));

        if (!verifyResponse.ok || verifyData?.data?.verified !== true) {
          setError("Current PIN is incorrect. Please try again.");
          return;
        }
      }

      const requestBody = {
        password: formData.password,
        pin: formData.pin,
      };

      const response = await fetch(getApiEndpoint("/v1/user/set-pin"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        setSuccess(true);
        // Reset form
        setFormData({ oldPin: "", password: "", pin: "" });

        // Close modal after showing success for a moment
        setTimeout(() => {
          setSuccess(false);
          onUpdate(); // Trigger parent component to refresh data
          onClose();
        }, 1500);
      } else {
        const data = await response.json();
        setError(
          data.message || `Failed to ${isReset ? "reset" : "set"} security PIN`
        );
      }
    } catch (err) {
      setError(`Failed to ${isReset ? "reset" : "set"} security PIN`);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPin = async () => {
    try {
      setLoading(true);
      setError(null);

      const jwt = await getVerifiedJWT();
      if (!jwt) {
        handleAuthError("Invalid jwt");
        return;
      }

      const response = await fetch(getApiEndpoint("/v1/user/forgot-pin"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        showSuccess(
          "A PIN reset link has been sent to your email. Please check your inbox."
        );
        handleCancel();
      } else {
        const data = await response.json();
        showError(data.message || "Failed to send PIN reset email");
      }
    } catch (err) {
      handleGenericError(err, "Failed to send PIN reset email");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form data
    setFormData({ oldPin: "", password: "", pin: "" });
    setError(null);
    setSuccess(false);
    onClose();
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleCancel}
      title={
        <Group gap="xs">
          <IconShieldCheck size={20} />
          <Title order={3} fw={600}>
            {isReset ? "Reset Security PIN" : "Set Security PIN"}
          </Title>
        </Group>
      }
      size="md"
      padding="xl"
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
    >
      <Stack gap="lg">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            icon={<IconShieldCheck size={16} />}
            title="Success"
            color="green"
            variant="light"
          >
            Security PIN has been set successfully!
          </Alert>
        )}

        {!success && (
          <>
            <Text c="dimmed" size="sm">
              {isReset
                ? "To reset your security PIN, you'll need to enter your current PIN and password, then create a new PIN."
                : "Create a 4-digit security PIN to secure your account. You'll need to confirm your current password to proceed."}
            </Text>

            <Divider />

            {/* Old PIN Section (only for reset) */}
            {isReset && (
              <Stack gap="sm">
                <Text fw={600} size="sm">
                  Enter Current PIN
                </Text>
                <Group justify="center">
                  <PinInput
                    length={4}
                    value={formData.oldPin}
                    onChange={value => handleInputChange("oldPin", value)}
                    placeholder="○"
                    type="number"
                    size="lg"
                    disabled={loading}
                    mask
                  />
                </Group>
              </Stack>
            )}

            {/* Password Section */}
            <Stack gap="sm">
              <Text fw={600} size="sm">
                Confirm Your Password
              </Text>
              <PasswordInput
                placeholder="Enter your current password"
                value={formData.password}
                onChange={event =>
                  handleInputChange("password", event.target.value)
                }
                disabled={loading}
                required
                leftSection={<IconLock size={16} />}
              />
            </Stack>

            {/* PIN Section */}
            <Stack gap="sm">
              <Text fw={600} size="sm">
                Create Your 4-Digit PIN
              </Text>
              <Group justify="center">
                <PinInput
                  length={4}
                  value={formData.pin}
                  onChange={value => handleInputChange("pin", value)}
                  placeholder="○"
                  type="number"
                  size="lg"
                  disabled={loading}
                  mask
                />
              </Group>
              <Text c="dimmed" size="xs" ta="center">
                Choose a PIN that you'll remember but others won't guess
              </Text>
            </Stack>

            {/* Forgot PIN Link (only for reset mode) */}
            {isReset && (
              <div style={{ textAlign: "center", marginTop: "10px" }}>
                <button
                  onClick={handleForgotPin}
                  disabled={loading}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#d0521f",
                    fontSize: "13px",
                    cursor: "pointer",
                    textDecoration: "underline",
                    padding: "4px 8px",
                  }}
                  type="button"
                >
                  Forgot PIN?
                </button>
              </div>
            )}

            {/* Action Buttons */}
            <Group justify="flex-end" gap="sm" pt="md">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                loading={loading}
                leftSection={
                  loading ? undefined : <IconShieldCheck size={16} />
                }
                disabled={
                  !formData.password ||
                  formData.pin.length !== 4 ||
                  (isReset && formData.oldPin.length !== 4)
                }
              >
                {isReset ? "Reset PIN" : "Set PIN"}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}

export default SetSecurityPinModal;
